import React, { useState, useEffect, useCallback } from 'react';
import StandardModal from './StandardModal';
import { getApiEndpoint, API_BASE_URL } from '../../../../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardCheck, faCalendarAlt, faUser, faBuilding,
  faFilePdf, faCircleCheck, faCircleXmark, faClock,
  faTriangleExclamation, faShieldHalved, faClockRotateLeft,
  faLocationDot, faBolt, faFlask, faNoteSticky, faWrench,
  faExternalLinkAlt, faSpinner, faFileCircleCheck, faImage,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { generateAssetReportPDF } from '../../../../utils/assetReportPDF';
import AssetDocumentsTab from '../../../shared/AssetDocumentsTab/AssetDocumentsTab';
import { openPdfInline } from '../../../shared/PdfViewer/PdfViewerHost';
import WrongAssetGuard from '../../../shared/WrongAssetGuard/WrongAssetGuard';
import PreTestChecklist from '../../../shared/PreTestChecklist/PreTestChecklist';
import './AssetDetailModal.css';

/* ── Asset-type labels ── */
const ASSET_TYPE_LABELS = {
  anchor_point: 'Anchor Point',
  cradle: 'Cradle',
  abseil_system: 'Abseil System',
  ladder: 'Ladder',
  handrail: 'Handrail',
  static_line: 'Static Line',
  walkway: 'Walkway',
  fixed_equipment: 'Fixed Equipment'
};

/* ── Compliance calc ── */
function getComplianceState(asset) {
  const status = (asset.status || '').toLowerCase();
  if (status === 'fail' || status === 'failed') {
    return { label: 'Failed — Attention Required', cls: 'adm-compliance-fail', icon: faCircleXmark };
  }

  const nextRaw = asset.next_inspection_date || asset.nextTestDate;
  if (!nextRaw) {
    const lastRaw = asset.last_inspection_date || asset.lastTestDate;
    if (!lastRaw || lastRaw === 'N/A') {
      return { label: 'Needs Testing', cls: 'adm-compliance-none', icon: faClipboardCheck };
    }
    return { label: 'No Due Date', cls: 'adm-compliance-unknown', icon: faClock };
  }

  const now = new Date();
  const next = new Date(nextRaw);
  if (isNaN(next.getTime())) return { label: 'Unknown', cls: 'adm-compliance-unknown', icon: faClock };

  const daysUntil = Math.ceil((next - now) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0)  return { label: 'Overdue', cls: 'adm-compliance-overdue', icon: faTriangleExclamation };
  if (daysUntil <= 30) return { label: 'Due Soon', cls: 'adm-compliance-due-soon', icon: faClock };
  if (status === 'pass' || status === 'passed') return { label: 'Compliant', cls: 'adm-compliance-pass', icon: faCircleCheck };
  if (status === 'pending') return { label: 'Pending', cls: 'adm-compliance-pending', icon: faClock };
  // Status is unknown/missing — don't claim compliance when we can't verify it.
  return { label: 'Unknown — Needs Testing', cls: 'adm-compliance-none', icon: faClipboardCheck };
}

/* ── Date formatting ── */
function fmtDate(d) {
  if (!d || d === 'N/A') return '—';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function fmtDateTime(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

/* ── Result badge ── */
function ResultBadge({ result }) {
  const r = (result || '').toLowerCase();
  const map = {
    pass: { cls: 'adm-result-pass', label: 'Pass' },
    passed: { cls: 'adm-result-pass', label: 'Pass' },
    fail: { cls: 'adm-result-fail', label: 'Fail' },
    failed: { cls: 'adm-result-fail', label: 'Fail' },
    pending: { cls: 'adm-result-pending', label: 'Pending' },
  };
  const m = map[r] || { cls: 'adm-result-unknown', label: result || 'Unknown' };
  return <span className={`adm-result-badge ${m.cls}`}>{m.label}</span>;
}

/* ═══════════════════════════════════════════════════════════════════
   AssetDetailModal — Reusable across all equipment types
   ═══════════════════════════════════════════════════════════════════ */
export default function AssetDetailModal({ isOpen, onClose, asset, assetType, propertyId, assetMeta }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!asset || !propertyId || !assetType) return;
    const assetId = asset.id || asset.asset_id;
    if (!assetId) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/test-history/${assetType}/${assetId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data = await res.json();
      setHistory(data.records || []);
    } catch (err) {
      console.error('Error loading test history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [asset, propertyId, assetType]);

  useEffect(() => {
    if (isOpen) fetchHistory();
    else { setHistory([]); setError(null); }
  }, [isOpen, fetchHistory]);

  if (!asset) return null;

  const compliance = getComplianceState(asset);
  const assetName = asset.name || asset.walkway_name || asset.cradle_name || asset.system_name || asset.ladder_id || asset.handrail_name || `${ASSET_TYPE_LABELS[assetType] || 'Asset'} #${asset.id}`;
  const latestRecord = history[0] || null;

  /* Helper to open a file URL.
     forcePdf=true: always use the inline viewer (use for test reports / certificates which are PDFs by spec).
     forcePdf=false: only open inline when URL ends in .pdf, otherwise new tab (for images etc). */
  const openFile = (url, fileTitle, forcePdf = true) => {
    if (!url) return;
    const full = url.startsWith('http') ? url : `${API_BASE_URL}/${url.replace(/^\//, '')}`;
    if (forcePdf || /\.pdf(\?|$)/i.test(full)) {
      openPdfInline(full, fileTitle || `${assetName} — Document`);
    } else {
      window.open(full, '_blank', 'noopener,noreferrer');
    }
  };

  /* Build meta rows from assetMeta prop (equipment-specific fields) */
  const metaFields = assetMeta || [];

  return (
    <StandardModal isOpen={isOpen} onClose={onClose} title="" size="large">
      <div className="adm-container">

        {/* Wrong-asset guard (P0-3): warns engineer if this asset isn't on their active job */}
        <WrongAssetGuard
          propertyId={propertyId}
          assetType={assetType}
          assetName={assetName}
        />

        {/* ── A) ASSET HEADER ── */}
        <div className="adm-header">
          <div className="adm-header-top">
            <div className="adm-header-left">
              <span className="adm-equipment-tag">{ASSET_TYPE_LABELS[assetType] || assetType}</span>
              <h1 className="adm-asset-name">{assetName}</h1>
              {asset.location && asset.location !== 'N/A' && (
                <p className="adm-location"><FontAwesomeIcon icon={faLocationDot} /> {asset.location}</p>
              )}
            </div>
            <div className="adm-header-right">
              <div className={`adm-compliance-chip ${compliance.cls}`}>
                <FontAwesomeIcon icon={compliance.icon} />
                <span>{compliance.label}</span>
              </div>
              <button
                className="adm-start-test-btn"
                type="button"
                onClick={() => setShowChecklist(s => !s)}
                title="Start a test on this asset (opens pre-test checklist)"
              >
                {showChecklist ? '✕ Hide checklist' : '⚡ Start test'}
              </button>
              <button
                className="adm-report-btn"
                type="button"
                onClick={() => {
                  try {
                    generateAssetReportPDF({
                      asset,
                      assetType,
                      propertyName: asset.property_name || asset.propertyName || '',
                      history,
                    });
                  } catch (err) {
                    console.error('Failed to generate asset report:', err);
                  }
                }}
                title="Download branded compliance report"
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Download report</span>
              </button>
            </div>
          </div>

          {/* Pre-test checklist (P0-4) — collapsed by default; expands on Start test */}
          {showChecklist && (
            <div className="adm-checklist-wrap">
              <PreTestChecklist
                propertyId={propertyId}
                assetType={assetType}
                assetId={asset.id || asset.asset_id}
                assetName={assetName}
                referenceId={asset.reference_id || asset.referenceId}
                externalReference={asset.external_ref || asset.external_reference}
              />
            </div>
          )}

          {/* Quick meta strip */}
          {metaFields.length > 0 && (
            <div className="adm-meta-strip">
              {metaFields.map((m, i) => m.value && m.value !== 'N/A' ? (
                <div key={i} className="adm-meta-item">
                  <span className="adm-meta-label">{m.label}</span>
                  <span className="adm-meta-value">{m.value}</span>
                </div>
              ) : null)}
            </div>
          )}
        </div>

        {/* ── B) LATEST TEST SUMMARY ── */}
        <div className="adm-section">
          <h2 className="adm-section-title">
            <FontAwesomeIcon icon={faShieldHalved} /> Latest Test Summary
          </h2>

          {loading && !latestRecord ? (
            <div className="adm-loading-inline"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
          ) : latestRecord ? (
            <div className="adm-latest-grid">
              <div className="adm-latest-item">
                <span className="adm-latest-label">Test Date</span>
                <span className="adm-latest-value">{fmtDate(latestRecord.test_date)}</span>
              </div>
              <div className="adm-latest-item">
                <span className="adm-latest-label">Result</span>
                <span className="adm-latest-value"><ResultBadge result={latestRecord.test_result} /></span>
              </div>
              <div className="adm-latest-item">
                <span className="adm-latest-label">Next Due</span>
                <span className="adm-latest-value">{fmtDate(latestRecord.next_test_due || asset.next_inspection_date || asset.nextTestDate)}</span>
              </div>
              <div className="adm-latest-item">
                <span className="adm-latest-label">Tested By</span>
                <span className="adm-latest-value">{latestRecord.tester_name || latestRecord.created_by_name || '—'}</span>
              </div>
              {latestRecord.tester_company && (
                <div className="adm-latest-item">
                  <span className="adm-latest-label">Company</span>
                  <span className="adm-latest-value">{latestRecord.tester_company}</span>
                </div>
              )}
              {latestRecord.test_type && (
                <div className="adm-latest-item">
                  <span className="adm-latest-label">Test Type</span>
                  <span className="adm-latest-value">{latestRecord.test_type}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="adm-empty-history">
              <FontAwesomeIcon icon={faClipboardCheck} className="adm-empty-icon" />
              <p className="adm-empty-text">No tests recorded for this asset</p>
              <span className="adm-empty-sub">Once testing is completed, results will appear here.</span>
            </div>
          )}
        </div>

        {/* ── C) TEST HISTORY ── */}
        <div className="adm-section">
          <h2 className="adm-section-title">
            <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
            {history.length > 0 && <span className="adm-history-count">{history.length} record{history.length !== 1 ? 's' : ''}</span>}
          </h2>

          {loading ? (
            <div className="adm-loading-inline"><FontAwesomeIcon icon={faSpinner} spin /> Loading test history…</div>
          ) : error ? (
            <div className="adm-error-inline">
              <span>Could not load test history.</span>
              <button className="adm-retry-btn" onClick={fetchHistory}>Retry</button>
            </div>
          ) : history.length === 0 ? (
            <div className="adm-empty-history">
              <FontAwesomeIcon icon={faClockRotateLeft} className="adm-empty-icon" />
              <p className="adm-empty-text">No test history available</p>
              <span className="adm-empty-sub">Historical test records will appear here as tests are completed.</span>
            </div>
          ) : (
            <div className="adm-timeline">
              {history.map((rec, idx) => (
                <div key={rec.uuid || rec.id || idx} className={`adm-timeline-entry ${idx === 0 ? 'adm-timeline-latest' : ''}`}>
                  {/* Timeline connector */}
                  <div className="adm-timeline-line">
                    <div className={`adm-timeline-dot ${(rec.test_result || '').toLowerCase() === 'pass' ? 'dot-pass' : (rec.test_result || '').toLowerCase() === 'fail' ? 'dot-fail' : 'dot-pending'}`} />
                  </div>

                  {/* Entry content */}
                  <div className="adm-timeline-content">
                    <div className="adm-timeline-head">
                      <span className="adm-timeline-date">{fmtDate(rec.test_date)}</span>
                      <ResultBadge result={rec.test_result} />
                      {rec.test_type && <span className="adm-test-type-tag">{rec.test_type}</span>}
                    </div>

                    <div className="adm-timeline-details">
                      {(rec.tester_name || rec.created_by_name) && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faUser} className="adm-detail-icon" />
                          <span>{rec.tester_name || rec.created_by_name}{rec.tester_company ? ` · ${rec.tester_company}` : ''}</span>
                        </div>
                      )}
                      {rec.tester_certification && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faFileCircleCheck} className="adm-detail-icon" />
                          <span>Cert: {rec.tester_certification}</span>
                        </div>
                      )}
                      {rec.test_method && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faFlask} className="adm-detail-icon" />
                          <span>Method: {rec.test_method}</span>
                        </div>
                      )}
                      {rec.test_load_applied && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faBolt} className="adm-detail-icon" />
                          <span>Load: {rec.test_load_applied} kN</span>
                        </div>
                      )}
                      {rec.next_test_due && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faCalendarAlt} className="adm-detail-icon" />
                          <span>Next due: {fmtDate(rec.next_test_due)}</span>
                        </div>
                      )}
                      {rec.regulation_reference && (
                        <div className="adm-detail-row">
                          <FontAwesomeIcon icon={faShieldHalved} className="adm-detail-icon" />
                          <span>{rec.regulation_reference}</span>
                        </div>
                      )}
                    </div>

                    {/* Findings */}
                    {(rec.defects_found || rec.remedial_action || rec.test_notes) && (
                      <div className="adm-timeline-findings">
                        {rec.defects_found && (
                          <div className="adm-finding-row adm-finding-defect">
                            <span className="adm-finding-label">Defects</span>
                            <p>{rec.defects_found}</p>
                          </div>
                        )}
                        {rec.remedial_action && (
                          <div className="adm-finding-row">
                            <span className="adm-finding-label">Remedial Action</span>
                            <p>{rec.remedial_action}</p>
                          </div>
                        )}
                        {rec.test_notes && (
                          <div className="adm-finding-row">
                            <span className="adm-finding-label">Notes</span>
                            <p>{rec.test_notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Documents / Evidence */}
                    {(rec.test_certificate_url || rec.test_report_url || rec.evidence_count > 0 || (rec.evidence_photos && rec.evidence_photos.length > 0)) && (
                      <div className="adm-timeline-docs">
                        {rec.test_certificate_url && (
                          <button className="adm-doc-btn" onClick={() => openFile(rec.test_certificate_url)}>
                            <FontAwesomeIcon icon={faFilePdf} /> Certificate
                          </button>
                        )}
                        {rec.test_report_url && (
                          <button className="adm-doc-btn" onClick={() => openFile(rec.test_report_url)}>
                            <FontAwesomeIcon icon={faFilePdf} /> Report
                          </button>
                        )}
                        {rec.evidence_count > 0 && (
                          <span className="adm-evidence-tag">
                            <FontAwesomeIcon icon={faImage} /> {rec.evidence_count} file{rec.evidence_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Audit trail */}
                    <div className="adm-timeline-audit">
                      {rec.source === 'external' && <span className="adm-source-tag">External</span>}
                      {rec.source === 'legacy' && <span className="adm-source-tag">Legacy</span>}
                      {rec.source_system && rec.source_system !== 'cloudsafe' && (
                        <span className="adm-source-tag">{rec.source_system}</span>
                      )}
                      {rec.created_at && (
                        <span className="adm-audit-timestamp">Recorded {fmtDateTime(rec.created_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── D) DOCUMENTS / EVIDENCE ON ASSET ── */}
        {(asset.test_report_url || asset.certificate_url) && (
          <div className="adm-section">
            <h2 className="adm-section-title">
              <FontAwesomeIcon icon={faFilePdf} /> Asset Documents
            </h2>
            <div className="adm-asset-docs">
              {asset.test_report_url && (
                <button className="adm-doc-btn-lg" onClick={() => openFile(asset.test_report_url)}>
                  <FontAwesomeIcon icon={faFilePdf} />
                  <span>Test Report</span>
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="adm-doc-external" />
                </button>
              )}
              {asset.certificate_url && (
                <button className="adm-doc-btn-lg" onClick={() => openFile(asset.certificate_url)}>
                  <FontAwesomeIcon icon={faFileCircleCheck} />
                  <span>Certificate</span>
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="adm-doc-external" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── E) ASSET-SCOPED DOCUMENTS (User Guide, Testing Spec, Examination Scheme, Method Statement) ── */}
        <div className="adm-section">
          <AssetDocumentsTab
            propertyId={propertyId}
            assetType={assetType}
            assetId={asset.id || asset.asset_id}
            assetName={assetName}
          />
        </div>

        {/* Notes from the asset itself */}
        {asset.notes && asset.notes !== '' && (
          <div className="adm-section">
            <h2 className="adm-section-title">
              <FontAwesomeIcon icon={faNoteSticky} /> Asset Notes
            </h2>
            <p className="adm-notes-text">{asset.notes}</p>
          </div>
        )}
      </div>
    </StandardModal>
  );
}
